import { GraftCodePrimitiveType } from '@graftcode/protocol'

import {
  CanonicalType,
  getCanonicalFromRawName,
  getTypeDisplay,
  PRIMITIVE_TO_CANONICAL,
} from '@/core/config/typeTranslations'

import {
  TYPE_USAGE_ARRAY,
  TYPE_USAGE_GENERIC,
  TYPE_USAGE_GENERIC_ALIAS,
  TYPE_USAGE_PRIMITIVE,
} from '../../../const/graftModelPayload.const'
import { UnifiedGraftModelTypeKey } from '../../../const/unifiedGraftModelTypeKeys.const'

import { getEffectiveTypeUsageKind, hasPayload, isNullable, nullable, resolveNode } from './nodeHelpers'
import type { NodeLike } from './types'

export function renderTypeUsage(typeUsage: NodeLike | null | undefined, technology: string | undefined): string {
  if (!typeUsage || typeof typeUsage !== 'object' || !Array.isArray(typeUsage.payload)) return ''
  const p = typeUsage.payload
  if (p.length === 0) return renderVoid(false, technology)
  const node = resolveNode(typeUsage)
  if (node) return renderNode(node, technology)
  if (typeof p[0] === 'string') return translateRawName(p[0], technology)
  return ''
}

function renderNode(node: NodeLike, technology: string | undefined): string {
  const kind = getEffectiveTypeUsageKind(node)
  const p = node.payload as unknown[]
  const nullableSuffix = nullable(p)

  switch (kind) {
    case UnifiedGraftModelTypeKey.TYPE_USAGE_VOID:
      return renderVoid(isNullable(p), technology)

    case UnifiedGraftModelTypeKey.TYPE_USAGE_PRIMITIVE: {
      const ns = p[TYPE_USAGE_PRIMITIVE.NAMESPACE] as string
      const name = p[TYPE_USAGE_PRIMITIVE.TYPENAME] as string
      const raw = ns ? `${ns}.${name}` : name
      const canonical = readPrimitiveCanonical(p)
      const display = canonical ? getTypeDisplay(canonical, technology) : undefined
      return (display ?? translateRawName(raw, technology)) + nullableSuffix
    }

    case UnifiedGraftModelTypeKey.TYPE_USAGE_GENERIC_ALIAS: {
      const aliasName = (p[TYPE_USAGE_GENERIC_ALIAS.ALIAS_NAME] as string) ?? 'unknown'
      return aliasName + nullableSuffix
    }

    case UnifiedGraftModelTypeKey.TYPE_USAGE_ARRAY: {
      const dimensions = Number(p[TYPE_USAGE_ARRAY.DIMENSION]) || 1
      const element = renderChild(p[TYPE_USAGE_ARRAY.ELEMENT_TYPE], technology)
      return `${element}${'[]'.repeat(dimensions)}${nullableSuffix}`
    }

    case UnifiedGraftModelTypeKey.TYPE_USAGE_GENERIC: {
      const name = (p[TYPE_USAGE_GENERIC.GENERIC_NAME] as string) ?? 'unknown'
      const args = p.slice(TYPE_USAGE_GENERIC.TYPE_ARGS_FROM).map((arg) => renderChild(arg, technology))
      const outer = translateRawName(name, technology)
      return `${outer}${args.length > 0 ? `<${args.join(', ')}>` : ''}${nullableSuffix}`
    }

    default:
      return 'unknown'
  }
}

function renderChild(child: unknown, technology: string | undefined): string {
  if (!hasPayload(child)) return 'unknown'
  return renderTypeUsage(child, technology)
}

function renderVoid(nullableFlag: boolean, technology: string | undefined): string {
  return getTypeDisplay('void', technology) + (nullableFlag ? '?' : '')
}

function readPrimitiveCanonical(payload: unknown[]): CanonicalType | undefined {
  if (payload.length <= TYPE_USAGE_PRIMITIVE.PRIMITIVE_TYPE) return undefined
  const codeRaw = payload[TYPE_USAGE_PRIMITIVE.PRIMITIVE_TYPE]
  if (codeRaw === undefined) return undefined
  const code = Number(codeRaw)
  if (Number.isNaN(code)) return undefined
  return PRIMITIVE_TO_CANONICAL[code as GraftCodePrimitiveType]
}

function translateRawName(raw: string, technology: string | undefined): string {
  if (!raw) return raw
  const canonical = getCanonicalFromRawName(raw)
  if (!canonical) return raw
  return getTypeDisplay(canonical, technology)
}
